import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter } from '../utils/newsletter';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedin,
  FaTiktok,
  FaWhatsapp,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const socialLinks = [
  { icon: <FaFacebookF />, label: 'Facebook', url: 'https://facebook.com' },
  { icon: <FaXTwitter />, label: 'X (formerly Twitter)', url: 'https://x.com' },
  { icon: <FaInstagram />, label: 'Instagram', url: 'https://instagram.com' },
  { icon: <FaLinkedin />, label: 'LinkedIn', url: 'https://linkedin.com' },
  { icon: <FaTiktok />, label: 'TikTok', url: 'https://tiktok.com' },
  {
    icon: <FaWhatsapp />,
    label: 'WhatsApp',
    url: 'https://wa.me/233247754531?text=Hi%20ASTEM%20Software%20Lab%2C%20I%20need%20assistance.',
  },
];

const PublicFooter = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubscribe = async (event) => {
    event.preventDefault();
    setStatus('submitting');
    setMessage('');

    try {
      await subscribeToNewsletter(email, 'footer');
      setEmail('');
      setStatus('success');
      setMessage('Thanks for subscribing!');
    } catch (error) {
      console.error('Newsletter subscription failed:', error);
      setStatus('error');
      setMessage(error.code === 'permission-denied' ? 'This email may already be subscribed.' : 'Subscription failed. Please try again.');
    }
  };

  return (
    <footer className="bg-primary text-white px-4 sm:px-6 py-8 mt-10 text-sm">
      <div className="max-w-7xl mx-auto grid gap-6 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <h3 className="text-xl font-bold mb-1">ASTEM Software Lab</h3>
          <p className="text-accent mb-4">Future Ready Software for a Smarter World</p>
          <div className="space-y-1 text-xs">
            <p><strong>Office:</strong> 23 Innovation Lane, Accra, Ghana</p>
            <p><strong>Hours:</strong> Mon - Fri, 8:00 AM - 5:00 PM</p>
            <p><strong>GPS:</strong> GA-123-4567</p>
            <p className="flex items-center gap-1">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] text-accent"><FaMapMarkerAlt /></span>
              <a
                href="https://maps.google.com/?q=23+Innovation+Lane,+Accra,+Ghana"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline hover:text-white"
              >
                View on Google Maps
              </a>
            </p>
          </div>
        </div>

        <div className="md:-translate-x-4">
          <h4 className="text-lg font-semibold mb-3">Quick Links</h4>
          <ul className="space-y-1">
            <li><Link to="/about" className="hover:text-accent">About Us</Link></li>
            <li><Link to="/privacy" className="hover:text-accent">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-accent">Terms of Service</Link></li>
          </ul>
        </div>

        <div className="md:-translate-x-3">
          <h4 className="text-lg font-semibold mb-3">Connect With Us</h4>
          <ul className="flex flex-nowrap items-center gap-1 lg:gap-1.5" aria-label="ASTEM social media links">
            {socialLinks.map(({ icon, label, url }) => (
              <li key={label}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow ASTEM on ${label}`}
                  title={label}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-xs text-white ring-1 ring-white/15 transition-all duration-200 hover:-translate-y-0.5 hover:bg-accent hover:text-primary hover:ring-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary lg:h-8 lg:w-8 lg:text-sm"
                >
                  {icon}
                  <span className="sr-only">{label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-3">Newsletter</h4>
          <p className="text-sm mb-3">Get updates straight to your inbox.</p>
          <form onSubmit={handleSubscribe} className="space-y-2">
            <input
              type="email"
              name="newsletter-email"
              placeholder="Enter your email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              className="px-3 py-2 w-full text-black rounded text-sm"
              required
              disabled={status === 'submitting'}
            />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full rounded-full bg-accent px-4 py-1.5 text-xs font-semibold transition hover:bg-white hover:text-primary disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
            >
              {status === 'submitting' ? 'Subscribing...' : 'Subscribe'}
            </button>
            {message && (
              <p role="status" aria-live="polite" className={status === 'error' ? 'text-red-200' : 'text-green-200'}>
                {message}
              </p>
            )}
          </form>
        </div>
      </div>

      <div className="mt-6 border-t border-white/20 pt-4 text-center text-xs px-4">
        &copy; {new Date().getFullYear()} ASTEM Software Lab. All rights reserved.
      </div>
    </footer>
  );
};

export default PublicFooter;
